import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDatabase, ref as dbRef, get, set, update } from "firebase/database";
import { getAuth } from 'firebase/auth';
import './SpaceSettings.css';
import FontsSettings from './components/FontsSettings';
import BackgroundColorChanger from './BackgroundColorChanger';
import ColorVault from './ColorVault';
import ColorPaletteGenerator from './components/ColorPaletteGenerator';
import ColorAssignmentSelector from './components/ColorAssignmentSelector';
import CloudflareDomainManager from './components/CloudflareDomainManager';
import { applySpaceSettingsToWidgetSync } from './utils/widgetStylingUtils';
import { 
    connectDomainToSpace, 
    disconnectDomainFromSpace, 
    getSpaceDomain,
    validateDomain,
    connectDomainToSpaceWithProxy,
    disconnectDomainFromSpaceWithProxy,
    generateProxyDNSInstructions,
    testCloudflareWorker
} from '../utils/domainHelpers';
import { 
    getSiteInfo
} from '../utils/firestorePublisher';
import { checkFirestoreSiteStatus } from '../utils/firestorePageLoader';
import { remove } from "firebase/database";
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useRef } from 'react';

/**
 * Space Settings Page - Globale Einstellungen für einen Space
 * Route: /space-settings/{spaceId}
 */
const SpaceSettings = () => {
    const { spaceId } = useParams();
    const navigate = useNavigate();
    const defaultBigFont = "Voyage";
    const defaultSmallFont = "Jost";

    const [spaceSettings, setSpaceSettings] = useState({
        spaceName: '',
        description: '',
        contextInfo: '', // Business context information
        ownerName: '', // Business owner name
        globalBigFont: defaultBigFont,
        globalSmallFont: defaultSmallFont,
        colors: {
            color1: '#3182CE', // Akzent - Kontrastfarbe für Highlights (ZUERST!)
            color2: '#2D3748', // Dunkel BG - Dunkler Hintergrund
            color3: '#4A5568', // Dunkel Text - Dunkler Text/Elemente
            color4: '#F7FAFC', // Hell Content - Heller Content-Bereich
            color5: '#FFFFFF', // Hell BG - Heller Hintergrund
        },
        colorAssignments: {
            background: 'color5',      // Heller Hintergrund
            primaryText: 'color2',     // Dunkler Text
            secondaryText: 'color3',   // Mittlerer Text
            buttons: 'color1',         // Akzent für Buttons
            buttonText: 'color5',      // Button-Text-Farbe
            accents: 'color1'          // Highlights & Akzente
        },
        backgroundColor: '#FFFFFF',
        customDomain: '',
        domainStatus: 'none',
        // NEW: Logo settings for custom branding
        customLogo: '',              // Logo URL
        customLogoWidth: 120,        // Logo width in px
        customLogoHeight: 60,        // Logo height in px
        showCustomLogo: true,        // Show logo in loading screen
        siteName: '',                // Custom site name for branding
        // NEW: SEO settings for custom domains
        customFavicon: '',           // Custom favicon URL
        siteTitle: '',               // Custom site title for browser tab
        siteDescription: '',         // Custom site description for meta tags
        ogImage: ''                  // Custom OG image for social media
    });

    const [activeTab, setActiveTab] = useState('design');
    const [isLoading, setIsLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [selectedFont, setSelectedFont] = useState(defaultBigFont);
    const [selectedSmallFont, setSelectedSmallFont] = useState(defaultSmallFont);
    const [spaceInfo, setSpaceInfo] = useState(null);
    
    // Domain Management States
    const [domainInfo, setDomainInfo] = useState(null);
    const [newDomain, setNewDomain] = useState('');
    const [connectingDomain, setConnectingDomain] = useState(false);
    const [showDNSInstructions, setShowDNSInstructions] = useState(false);
    const [dnsInstructions, setDnsInstructions] = useState(null);
    
    // Firestore Integration States
    const [firestoreSiteInfo, setFirestoreSiteInfo] = useState(null);
    const [firestoreStatus, setFirestoreStatus] = useState(null);
    
    // Logo Upload States
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [logoUploadProgress, setLogoUploadProgress] = useState(0);
    const [logoUploadSuccess, setLogoUploadSuccess] = useState(false);
    const logoInputRef = useRef(null);

    // Upload handlers for Custom Meta
    const handleFaviconUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setIsLoading(true);
            const storage = getStorage();
            const faviconRef = storageRef(storage, `spaces/${spaceId}/favicon/${file.name}`);
            const uploadTask = uploadBytesResumable(faviconRef, file);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    // Progress tracking could be added here
                },
                (error) => {
                    console.error('❌ Favicon upload error:', error);
                    alert('Error uploading favicon');
                    setIsLoading(false);
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    handleSettingsChange('customFavicon', downloadURL);
                    setIsLoading(false);
                }
            );
        } catch (error) {
            console.error('❌ Favicon upload error:', error);
            setIsLoading(false);
        }
    };

    const handleOGImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setIsLoading(true);
            const storage = getStorage();
            const ogRef = storageRef(storage, `spaces/${spaceId}/og-image/${file.name}`);
            const uploadTask = uploadBytesResumable(ogRef, file);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    // Progress tracking could be added here
                },
                (error) => {
                    console.error('❌ OG Image upload error:', error);
                    alert('Error uploading OG image');
                    setIsLoading(false);
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    handleSettingsChange('ogImage', downloadURL);
                    setIsLoading(false);
                }
            );
        } catch (error) {
            console.error('❌ OG Image upload error:', error);
            setIsLoading(false);
        }
    };
    const [domainError, setDomainError] = useState('');
    const [useCloudflareProxy] = useState(true); // Immer aktiviert
    const [workerStatus, setWorkerStatus] = useState(null);
    const [showCloudflareDomainManager, setShowCloudflareDomainManager] = useState(false);
    
    // Color Generator Toggle State
    const [showColorGenerator, setShowColorGenerator] = useState(false);
    const [hasSavedPalettes, setHasSavedPalettes] = useState(false);
    
    // Font Change Tracking States
    const [originalFonts, setOriginalFonts] = useState({
        globalBigFont: defaultBigFont,
        globalSmallFont: defaultSmallFont
    });
    const [fontsChanged, setFontsChanged] = useState(false);
    const [bigFontChanged, setBigFontChanged] = useState(false);
    const [smallFontChanged, setSmallFontChanged] = useState(false);
    
    // Color Change Tracking States
    const [originalColors, setOriginalColors] = useState({
        color1: '#3182CE',
        color2: '#2D3748', 
        color3: '#4A5568',
        color4: '#F7FAFC',
        color5: '#FFFFFF'
    });
    const [originalColorAssignments, setOriginalColorAssignments] = useState({
        background: 'color5',
        primaryText: 'color2',
        secondaryText: 'color3',
        buttons: 'color1',
        buttonText: 'color5',
        accents: 'color1'
    });
    const [colorsChanged, setColorsChanged] = useState(false);
    const [colorAssignmentsChanged, setColorAssignmentsChanged] = useState(false);
    
    // Delete Space States
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [pages, setPages] = useState([]);

    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;

    // Load Space Settings on mount
    useEffect(() => {
        if (currentUser && spaceId) {
            loadSpaceSettings();
            loadSpaceDomain();
            checkCloudflareWorkerStatus();
            loadSpacePages();
            checkSavedPalettes();
            
            // DEBUG: Mache Debug-Tools in Browser-Konsole verfügbar
            window.debugSpaceDomain = {
                userId: currentUser.uid,
                spaceId: spaceId,
                checkSpace: async () => {
                    const spaceRef = dbRef(db, `/users/${currentUser.uid}/spaces/${spaceId}`);
                    const snapshot = await get(spaceRef);
                    console.log('🔍 Space data:', snapshot.exists() ? snapshot.val() : 'NOT FOUND');
                    return snapshot.exists() ? snapshot.val() : null;
                },
                checkDomainMappings: async () => {
                    const mappingsRef = dbRef(db, '/domainMappings');
                    const snapshot = await get(mappingsRef);
                    console.log('🔍 All domain mappings:', snapshot.exists() ? snapshot.val() : 'NONE');
                    return snapshot.exists() ? snapshot.val() : null;
                },
                testGetSpaceDomain: async () => {
                    const result = await getSpaceDomain(currentUser.uid, spaceId);
                    console.log('🔍 getSpaceDomain result:', result);
                    return result;
                }
            };
            console.log('🛠️ Debug tools available: window.debugSpaceDomain');
        } else if (!currentUser) {
            navigate('/login');
        }
    }, [currentUser, spaceId, navigate]);

    // 🔤 FONT TRACKING: Monitor font changes via useEffect (separate tracking)
    useEffect(() => {
        const bigFontHasChanged = spaceSettings.globalBigFont !== originalFonts.globalBigFont;
        const smallFontHasChanged = spaceSettings.globalSmallFont !== originalFonts.globalSmallFont;
        const anyFontChanged = bigFontHasChanged || smallFontHasChanged;
        
        // Update individual font change states
        if (bigFontHasChanged !== bigFontChanged) {
            setBigFontChanged(bigFontHasChanged);
        }
        if (smallFontHasChanged !== smallFontChanged) {
            setSmallFontChanged(smallFontHasChanged);
        }
        if (anyFontChanged !== fontsChanged) {
            setFontsChanged(anyFontChanged);
        }
        
        console.log('🔤 [FONT-TRACKING] Font change detected via useEffect:', {
            originalFonts,
            currentFonts: {
                globalBigFont: spaceSettings.globalBigFont,
                globalSmallFont: spaceSettings.globalSmallFont
            },
            bigFontChanged: bigFontHasChanged,
            smallFontChanged: smallFontHasChanged,
            anyFontChanged: anyFontChanged
        });
    }, [spaceSettings.globalBigFont, spaceSettings.globalSmallFont, originalFonts, fontsChanged, bigFontChanged, smallFontChanged]);

    const loadSpaceSettings = async () => {
        if (!currentUser || !spaceId) {
            console.log('❌ Missing data for loading space settings:', { currentUser: !!currentUser, spaceId });
            return;
        }
        
        console.log('🔄 Loading space settings for:', { userId: currentUser.uid, spaceId });
        setIsLoading(true);
        try {
            // Load space info
            const spaceRef = dbRef(db, `/users/${currentUser.uid}/spaces/${spaceId}`);
            const spaceSnapshot = await get(spaceRef);
            
            if (spaceSnapshot.exists()) {
                const spaceData = spaceSnapshot.val();
                console.log('📊 Loaded space settings:', spaceData);
                console.log('🎨 Design settings check:', {
                    hasDesignSettings: !!spaceData.designSettings,
                    designSettings: spaceData.designSettings,
                    directColors: spaceData.colors,
                    directFonts: { big: spaceData.globalBigFont, small: spaceData.globalSmallFont }
                });
                
                setSpaceInfo(spaceData);
                const loadedBigFont = spaceData.designSettings?.globalBigFont || spaceData.globalBigFont || defaultBigFont;
                const loadedSmallFont = spaceData.designSettings?.globalSmallFont || spaceData.globalSmallFont || defaultSmallFont;
                
                // 🔤 FONT TRACKING: Store original fonts to detect changes
                setOriginalFonts({
                    globalBigFont: loadedBigFont,
                    globalSmallFont: loadedSmallFont
                });
                
                // 🎨 COLOR TRACKING: Store original colors and assignments to detect changes
                const loadedColors = spaceData.designSettings?.colors || spaceData.colors || {
                    color1: '#3182CE', color2: '#2D3748', color3: '#4A5568', color4: '#F7FAFC', color5: '#FFFFFF'
                };
                const loadedColorAssignments = spaceData.designSettings?.colorAssignments || {
                    background: 'color5', primaryText: 'color2', secondaryText: 'color3', 
                    buttons: 'color1', buttonText: 'color5', accents: 'color1'
                };
                
                setOriginalColors(loadedColors);
                setOriginalColorAssignments(loadedColorAssignments);
                
                console.log('🎨 [COLOR-TRACKING] Original colors stored:', loadedColors);
                console.log('🎨 [COLOR-TRACKING] Original assignments stored:', loadedColorAssignments);
                
                setSpaceSettings(prevSettings => ({
                    ...prevSettings,
                    spaceName: spaceData.spaceName || '',
                    description: spaceData.description || '',
                    contextInfo: spaceData.designSettings?.contextInfo || '',
                    ownerName: spaceData.designSettings?.ownerName || '',
                    globalBigFont: loadedBigFont,
                    globalSmallFont: loadedSmallFont,
                    colors: spaceData.designSettings?.colors || spaceData.colors || prevSettings.colors,
                    colorAssignments: spaceData.designSettings?.colorAssignments || prevSettings.colorAssignments, // ✅ Color Assignments laden
                    backgroundColor: spaceData.designSettings?.backgroundColor || spaceData.backgroundColor || '#FFFFFF',
                    customDomain: spaceData.customDomain || '',
                    domainStatus: spaceData.domainStatus || 'none',
                    
                    // ✅ BRANDING SETTINGS LADEN
                    customLogo: spaceData.customLogo || '',
                    customLogoWidth: spaceData.customLogoWidth || 120,
                    customLogoHeight: spaceData.customLogoHeight || 60,
                    showCustomLogo: spaceData.showCustomLogo !== false,
                    siteName: spaceData.siteName || '',
                    
                    // ✅ CUSTOM META SETTINGS LADEN
                    customFavicon: spaceData.customFavicon || '',
                    siteTitle: spaceData.siteTitle || '',
                    siteDescription: spaceData.siteDescription || '',
                    ogImage: spaceData.ogImage || ''
                }));
                
                setSelectedFont(spaceData.designSettings?.globalBigFont || spaceData.globalBigFont || defaultBigFont);
                setSelectedSmallFont(spaceData.designSettings?.globalSmallFont || spaceData.globalSmallFont || defaultSmallFont);
                
                console.log('🔤 Font settings applied:', {
                    bigFont: spaceData.designSettings?.globalBigFont || spaceData.globalBigFont || defaultBigFont,
                    smallFont: spaceData.designSettings?.globalSmallFont || spaceData.globalSmallFont || defaultSmallFont
                });
                
                console.log('🎨 Branding settings loaded:', {
                    customLogo: spaceData.customLogo ? 'present' : 'missing',
                    logoUrl: spaceData.customLogo,
                    customLogoWidth: spaceData.customLogoWidth,
                    customLogoHeight: spaceData.customLogoHeight,
                    showCustomLogo: spaceData.showCustomLogo,
                    siteName: spaceData.siteName
                });
            } else {
                console.log('⚠️ No space data found, redirecting to dashboard');
                navigate('/pages');
            }
        } catch (error) {
            console.error('❌ Error loading space settings:', error);
            navigate('/pages');
        } finally {
            setIsLoading(false);
        }
    };

    const loadSpaceDomain = async () => {
        if (!currentUser || !spaceId) return;
        
        try {
            console.log('🔄 Loading space domain for:', { userId: currentUser.uid, spaceId });
            
            // DEBUG: Prüfe Space-Daten direkt
            const spaceRef = dbRef(db, `/users/${currentUser.uid}/spaces/${spaceId}`);
            const spaceSnapshot = await get(spaceRef);
            
            if (spaceSnapshot.exists()) {
                const spaceData = spaceSnapshot.val();
                console.log('🔍 [DEBUG] Raw space data:', spaceData);
                console.log('🔍 [DEBUG] customDomain in space:', spaceData.customDomain);
                console.log('🔍 [DEBUG] domainStatus in space:', spaceData.domainStatus);
                
                // Prüfe auch domainMappings direkt
                if (spaceData.customDomain) {
                    const escapedDomain = spaceData.customDomain.replace(/\./g, '_DOT_').replace(/[#$\[\]]/g, '_');
                    console.log('🔍 [DEBUG] Escaped domain for Firebase:', escapedDomain);
                    
                    const domainMappingRef = dbRef(db, `/domainMappings/${escapedDomain}`);
                    const domainSnapshot = await get(domainMappingRef);
                    
                    console.log('🔍 [DEBUG] Domain mapping exists:', domainSnapshot.exists());
                    if (domainSnapshot.exists()) {
                        console.log('🔍 [DEBUG] Domain mapping data:', domainSnapshot.val());
                    }
                }
            } else {
                console.log('❌ [DEBUG] Space not found in Firebase');
            }
            
            // Verwende die normale getSpaceDomain Funktion
            const domain = await getSpaceDomain(currentUser.uid, spaceId);
            setDomainInfo(domain);
            console.log('✅ Space domain loaded:', domain);
            
            // Update spaceSettings with domain info if found
            if (domain) {
                setSpaceSettings(prev => ({
                    ...prev,
                    customDomain: domain.domain,
                    domainStatus: domain.domainStatus || 'pending'
                }));
            } else {
                console.log('⚠️ [DEBUG] No domain found - checking if space has customDomain field');
                if (spaceSnapshot.exists()) {
                    const spaceData = spaceSnapshot.val();
                    if (spaceData.customDomain) {
                        console.log('🔍 [DEBUG] Space has customDomain but getSpaceDomain returned null');
                        console.log('🔍 [DEBUG] This suggests domain mapping is missing or corrupted');
                        
                        // Fallback: Zeige Domain aus Space-Daten auch wenn Mapping fehlt
                        setDomainInfo({
                            domain: spaceData.customDomain,
                            domainStatus: spaceData.domainStatus || 'pending',
                            mapping: null // Mapping fehlt
                        });
                        
                        setSpaceSettings(prev => ({
                            ...prev,
                            customDomain: spaceData.customDomain,
                            domainStatus: spaceData.domainStatus || 'pending'
                        }));
                    }
                }
            }
        } catch (error) {
            console.error('❌ Error loading space domain:', error);
            setDomainInfo(null);
        }
    };

    const checkCloudflareWorkerStatus = async () => {
        try {
            const status = await testCloudflareWorker();
            setWorkerStatus(status);
            console.log('🧪 Cloudflare Worker Status:', status);
        } catch (error) {
            console.warn('⚠️ Could not check Cloudflare Worker status:', error);
            setWorkerStatus({ success: false, error: error.message });
        }
    };

    const checkSavedPalettes = async () => {
        if (!currentUser || !spaceId) return;
        
        try {
            const palettesPath = `/users/${currentUser.uid}/spaces/${spaceId}/savedColorPalettes`;
            const palettesRef = dbRef(db, palettesPath);
            const snapshot = await get(palettesRef);
            
            const hasExistingPalettes = snapshot.exists() && Object.keys(snapshot.val()).length > 0;
            setHasSavedPalettes(hasExistingPalettes);
            
            console.log('🎨 [PALETTE-CHECK] Has saved palettes:', hasExistingPalettes);
        } catch (error) {
            console.error('❌ Error checking saved palettes:', error);
            setHasSavedPalettes(false);
        }
    };

    const handleConnectDomain = async () => {
        if (!newDomain.trim() || !currentUser || !spaceId) return;
        
        setConnectingDomain(true);
        setDomainError('');
        
        try {
            const validation = validateDomain(newDomain);
            if (!validation.valid) {
                setDomainError(validation.error);
                setConnectingDomain(false);
                return;
            }

            let result;
            
            if (useCloudflareProxy && workerStatus?.success) {
                console.log('🚀 Connecting domain with Cloudflare proxy...');
                result = await connectDomainToSpaceWithProxy(
                    currentUser.uid, 
                    spaceId, 
                    validation.domain, 
                    spaceInfo?.spaceName || spaceId, 
                    spaceInfo?.username || spaceId  // ✅ Fallback to spaceId
                );
            } else {
                console.log('⚡ Connecting domain with Firebase hosting...');
                result = await connectDomainToSpace(
                    currentUser.uid, 
                    spaceId, 
                    validation.domain, 
                    spaceInfo?.spaceName || spaceId, 
                    spaceInfo?.username || spaceId  // ✅ Fallback to spaceId
                );
            }
            
            await loadSpaceDomain();
            setNewDomain('');
            setDomainError('');
            
            // Generate and show DNS instructions
            if (result.proxyEnabled) {
                const instructions = generateProxyDNSInstructions(validation.domain);
                setDnsInstructions(instructions);
                setShowDNSInstructions(true);
            }
            
            console.log('✅ Domain connected successfully:', {
                domain: validation.domain,
                proxyEnabled: result.proxyEnabled
            });
            
        } catch (error) {
            console.error('❌ Error connecting domain:', error);
            setDomainError(error.message || 'Failed to connect domain');
        } finally {
            setConnectingDomain(false);
        }
    };

    const handleDisconnectDomain = async () => {
        if (!domainInfo?.domain || !currentUser || !spaceId) return;
        
        const proxyText = domainInfo.mapping?.proxyEnabled ? ' (Unlimited Proxy)' : '';
        const confirmDisconnect = window.confirm(
            `Disconnect ${domainInfo.domain}${proxyText} from this space?`
        );
        
        if (!confirmDisconnect) return;
        
        try {
            console.log('🔄 Attempting to disconnect domain:', {
                domain: domainInfo.domain,
                userId: currentUser.uid,
                spaceId,
                proxyEnabled: domainInfo.mapping?.proxyEnabled
            });
            
            // Versuche zuerst die einfache Disconnect-Methode
            await disconnectDomainFromSpace(currentUser.uid, spaceId, domainInfo.domain);
            
            // Falls Proxy aktiv war, versuche Proxy-Cleanup (aber nicht kritisch)
            if (domainInfo.mapping?.proxyEnabled) {
                try {
                    await disconnectDomainFromSpaceWithProxy(currentUser.uid, spaceId, domainInfo.domain);
                } catch (proxyError) {
                    console.warn('⚠️ Proxy cleanup failed (not critical):', proxyError.message);
                    // Domain ist trotzdem disconnected, Proxy-Cleanup ist optional
                }
            }
            
            await loadSpaceDomain();
            console.log('✅ Domain disconnected successfully');
            alert('Domain successfully disconnected!');
            
        } catch (error) {
            console.error('❌ Error disconnecting domain:', error);
            
            // Fallback: Manuelles Cleanup der Space-Settings
            try {
                console.log('🔧 Attempting manual space cleanup...');
                const spaceRef = dbRef(db, `/users/${currentUser.uid}/spaces/${spaceId}`);
                const spaceSnapshot = await get(spaceRef);
                
                if (spaceSnapshot.exists()) {
                    const spaceData = spaceSnapshot.val();
                    const { customDomain, domainStatus, ...cleanSpaceData } = spaceData;
                    
                    await set(spaceRef, {
                        ...cleanSpaceData,
                        updatedAt: new Date().toISOString()
                    });
                    
                    console.log('✅ Manual space cleanup successful');
                    await loadSpaceDomain();
                    alert('Domain disconnected (manual cleanup)');
                } else {
                    throw new Error('Space not found');
                }
            } catch (fallbackError) {
                console.error('❌ Manual cleanup also failed:', fallbackError);
                alert(`Failed to disconnect domain: ${error.message}`);
            }
        }
    };

    const handleSettingsChange = (field, value) => {
        setSpaceSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleBigFontChange = (font) => {
        setSelectedFont(font);
        handleSettingsChange('globalBigFont', font);
        // Font change tracking happens automatically via useEffect
    };

    const handleSmallFontChange = (font) => {
        setSelectedSmallFont(font);
        handleSettingsChange('globalSmallFont', font);
        // Font change tracking happens automatically via useEffect
    };

    const handleColorChange = (colorKey, colorValue) => {
        const newColors = {
            ...spaceSettings.colors,
            [colorKey]: colorValue
        };
        
        setSpaceSettings(prev => ({
            ...prev,
            colors: newColors
        }));
        
        // 🎨 COLOR TRACKING: Check if colors have changed
        const hasColorsChanged = JSON.stringify(newColors) !== JSON.stringify(originalColors);
        setColorsChanged(hasColorsChanged);
        
        console.log('🎨 [COLOR-CHANGE] Color changed:', {
            colorKey,
            from: spaceSettings.colors[colorKey],
            to: colorValue,
            allColorsChanged: hasColorsChanged
        });
    };

    const handleBackgroundColorChange = (color) => {
        handleSettingsChange('backgroundColor', color);
    };

    // Load pages for delete preview
    const loadSpacePages = async () => {
        if (!currentUser || !spaceId) return;
        
        try {
            const pagesRef = dbRef(db, `/pages/${currentUser.uid}/${spaceId}`);
            const snapshot = await get(pagesRef);
            
            if (snapshot.exists()) {
                const pagesData = snapshot.val();
                const pagesArray = Object.entries(pagesData).map(([key, data]) => ({
                    ...data,
                    pageName: key,
                    id: `${currentUser.uid}/${spaceId}/${key}`
                }));
                setPages(pagesArray);
            } else {
                setPages([]);
            }
        } catch (error) {
            console.error('Error loading space pages:', error);
            setPages([]);
        }
    };

    // Delete Space Function
    const handleDeleteSpace = async () => {
        if (!currentUser || !spaceInfo?.username || !spaceId) return;

        setDeleting(true);
        try {
            console.log('🗑️ Deleting space:', { username: spaceInfo.username, spaceId, pageCount: pages.length });

            // Delete all pages
            for (const page of pages) {
                const pageKey = page.pageName;
                const pageRef = dbRef(db, `/pages/${currentUser.uid}/${spaceId}/${pageKey}`);
                await remove(pageRef);
                
                const pagePath = page.slug === "/" ? "" : (page.slug || page.pageName);
                const urlMappingRef = dbRef(db, `/urlMappings/${spaceInfo.username}/${pagePath}`);
                await remove(urlMappingRef);
            }

            // Delete space
            const spaceRef = dbRef(db, `/users/${currentUser.uid}/spaces/${spaceId}`);
            await remove(spaceRef);

            // Delete spacename mapping
            const spacenameRef = dbRef(db, `/spacenames/${spaceInfo.username}`);
            await remove(spacenameRef);

            console.log('✅ Space deleted successfully');
            navigate('/pages'); // Navigate back to spaces overview
        } catch (error) {
            console.error('Error deleting space:', error);
            alert('Failed to delete space. Please try again.');
        } finally {
            setDeleting(false);
            setShowDeleteModal(false);
        }
    };

    // Logo Upload Handler with Navigation Prevention
    const handleLogoUpload = async (event) => {
        // CRITICAL: Prevent form submission and navigation
        event.preventDefault();
        event.stopPropagation();
        
        const file = event.target.files[0];
        if (!file) {
            console.log("Keine Logo-Datei ausgewählt.");
            return;
        }

        console.log('📤 Starting logo upload:', { fileName: file.name, fileSize: file.size });

        // Immediately reset the input to prevent re-triggering
        const inputElement = event.target;
        setTimeout(() => {
            if (inputElement) {
                inputElement.value = '';
            }
        }, 100);

        // File validation
        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            alert('Logo file is too large. Please choose a file smaller than 5MB.');
            // Reset file input
            event.target.value = '';
            return;
        }

        if (!file.type.startsWith('image/')) {
            alert('Please select a valid image file.');
            // Reset file input
            event.target.value = '';
            return;
        }

        setIsUploadingLogo(true);
        setLogoUploadProgress(0);

        try {
            const storage = getStorage();
            const logoPath = `spaces/${currentUser.uid}/${spaceId}/logo_${Date.now()}_${file.name}`;
            const logoRef = storageRef(storage, logoPath);

            const uploadTask = uploadBytesResumable(logoRef, file);

            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setLogoUploadProgress(progress);
                    console.log('📤 Logo upload progress:', Math.round(progress) + '%');
                },
                (error) => {
                    console.error('❌ Logo upload error:', error);
                    alert('Logo upload failed. Please try again.');
                    setIsUploadingLogo(false);
                    setLogoUploadProgress(0);
                },
                async () => {
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        
                        // 🔥 NEW: Load image to get actual dimensions
                        const img = new Image();
                        img.onload = async () => {
                            const actualWidth = img.naturalWidth;
                            const actualHeight = img.naturalHeight;
                            
                            // Calculate scaled dimensions to fit loading screen nicely
                            // Max: 300px width, 150px height
                            let scaledWidth = actualWidth;
                            let scaledHeight = actualHeight;
                            
                            const maxWidth = 300;
                            const maxHeight = 150;
                            
                            if (scaledWidth > maxWidth) {
                                const ratio = maxWidth / scaledWidth;
                                scaledWidth = maxWidth;
                                scaledHeight = Math.round(scaledHeight * ratio);
                            }
                            
                            if (scaledHeight > maxHeight) {
                                const ratio = maxHeight / scaledHeight;
                                scaledHeight = maxHeight;
                                scaledWidth = Math.round(scaledWidth * ratio);
                            }
                            
                            // Round to nearest 10 for width, 5 for height (for slider steps)
                            scaledWidth = Math.round(scaledWidth / 10) * 10;
                            scaledHeight = Math.round(scaledHeight / 5) * 5;
                            
                            console.log('📐 Logo dimensions:', {
                                original: `${actualWidth}x${actualHeight}`,
                                scaled: `${scaledWidth}x${scaledHeight}`
                            });
                            
                            // Update space settings with new logo + dimensions
                            setSpaceSettings(prev => ({
                                ...prev,
                                customLogo: downloadURL,
                                customLogoWidth: scaledWidth,
                                customLogoHeight: scaledHeight,
                                showCustomLogo: true // Auto-enable logo display
                            }));
                            
                            console.log('✅ Logo uploaded successfully:', downloadURL);
                            setIsUploadingLogo(false);
                            setLogoUploadProgress(0);
                            
                            // Show success feedback
                            setLogoUploadSuccess(true);
                            setTimeout(() => setLogoUploadSuccess(false), 3000);
                            
                            // Reset file input to allow same file upload again
                            if (event.target) {
                                event.target.value = '';
                            }
                            
                            // Auto-save after logo upload (immediate, no delay needed)
                            console.log('💾 Auto-saving space settings after logo upload...');
                            await saveSpaceSettingsQuiet();
                        };
                        
                        img.onerror = () => {
                            console.warn('⚠️ Could not load image dimensions, using defaults');
                            // Fallback: Use default dimensions
                            setSpaceSettings(prev => ({
                                ...prev,
                                customLogo: downloadURL,
                                customLogoWidth: 120,
                                customLogoHeight: 60,
                                showCustomLogo: true
                            }));
                            
                            setIsUploadingLogo(false);
                            setLogoUploadProgress(0);
                            setLogoUploadSuccess(true);
                            setTimeout(() => setLogoUploadSuccess(false), 3000);
                            
                            if (event.target) {
                                event.target.value = '';
                            }
                        };
                        
                        img.src = downloadURL;
                        
                    } catch (error) {
                        console.error('❌ Error getting logo download URL:', error);
                        alert('Error processing uploaded logo. Please try again.');
                        setIsUploadingLogo(false);
                        setLogoUploadProgress(0);
                        // Reset file input on error
                        if (event.target) {
                            event.target.value = '';
                        }
                    }
                }
            );
        } catch (error) {
            console.error('❌ Logo upload initialization error:', error);
            alert('Error starting logo upload. Please try again.');
            setIsUploadingLogo(false);
            setLogoUploadProgress(0);
            // Reset file input on error
            if (event.target) {
                event.target.value = '';
            }
        }
    };

    // Alternative: Button-triggered Logo Upload (prevents navigation issues)
    const triggerLogoUpload = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        console.log('🎯 Triggering logo upload via button click');
        
        if (logoInputRef.current) {
            logoInputRef.current.click();
        }
    };

    // Quiet save function for logo uploads (no navigation, no loading state)
    const saveSpaceSettingsQuiet = async () => {
        if (!currentUser || !spaceId) return;

        try {
            // Prepare design settings for proper structure
            const designSettings = {
                ownerName: spaceSettings.ownerName,
                contextInfo: spaceSettings.contextInfo,
                globalBigFont: spaceSettings.globalBigFont,
                globalSmallFont: spaceSettings.globalSmallFont,
                colors: spaceSettings.colors,
                colorAssignments: spaceSettings.colorAssignments,
                backgroundColor: spaceSettings.backgroundColor
            };

            // Save space settings (PRESERVE savedColorPalettes!)
            const spaceRef = dbRef(db, `/users/${currentUser.uid}/spaces/${spaceId}`);
            
            // Load current space data to preserve savedColorPalettes
            const currentSpaceSnapshot = await get(spaceRef);
            const currentSpaceData = currentSpaceSnapshot.exists() ? currentSpaceSnapshot.val() : {};
            
            const updatedSpaceData = {
                ...spaceInfo,
                spaceName: spaceSettings.spaceName,
                description: spaceSettings.description,
                customDomain: spaceSettings.customDomain,
                domainStatus: spaceSettings.domainStatus,
                designSettings: designSettings,
                
                // NEW: Logo and branding settings
                customLogo: spaceSettings.customLogo,
                customLogoWidth: spaceSettings.customLogoWidth,
                customLogoHeight: spaceSettings.customLogoHeight,
                showCustomLogo: spaceSettings.showCustomLogo,
                siteName: spaceSettings.siteName,
                
                updatedAt: new Date().toISOString(),
                
                // 🛡️ PRESERVE EXISTING SAVED PALETTES
                savedColorPalettes: currentSpaceData.savedColorPalettes || {}
            };
            
            await set(spaceRef, updatedSpaceData);
            
            // 🔥 NEW: Also copy logo settings to public space for unauthenticated access
            if (spaceSettings.customLogo || spaceSettings.siteName) {
                const publicSpaceRef = dbRef(db, `/publicSpaces/${spaceId}`);
                
                // Load existing public space data to preserve other settings
                const existingPublicSnapshot = await get(publicSpaceRef);
                const existingPublicData = existingPublicSnapshot.exists() ? existingPublicSnapshot.val() : {};
                
                const updatedPublicData = {
                    ...existingPublicData,
                    // Update logo settings
                    customLogo: spaceSettings.customLogo || '',
                    customLogoWidth: spaceSettings.customLogoWidth || 120,
                    customLogoHeight: spaceSettings.customLogoHeight || 60,
                    showCustomLogo: spaceSettings.showCustomLogo !== false,
                    siteName: spaceSettings.siteName || spaceSettings.spaceName || '',
                    updatedAt: new Date().toISOString()
                };
                
                await set(publicSpaceRef, updatedPublicData);
                console.log('🎨 [LOGO-SAVE] Logo settings copied to public space for custom domains');
            }
            
            console.log('✅ Space settings saved quietly (logo upload)');
            
        } catch (error) {
            console.error('❌ Error saving space settings quietly:', error);
        }
    };

    const saveSpaceSettings = async () => {
        if (!currentUser || !spaceId) return;

        setIsLoading(true);
        try {
            // Prepare design settings for proper structure
            const designSettings = {
                ownerName: spaceSettings.ownerName,
                contextInfo: spaceSettings.contextInfo,
                globalBigFont: spaceSettings.globalBigFont,
                globalSmallFont: spaceSettings.globalSmallFont,
                colors: spaceSettings.colors,
                colorAssignments: spaceSettings.colorAssignments, // ✅ Color Assignments speichern
                backgroundColor: spaceSettings.backgroundColor
            };

            // Save space settings (PRESERVE savedColorPalettes!)
            const spaceRef = dbRef(db, `/users/${currentUser.uid}/spaces/${spaceId}`);
            
            // Load current space data to preserve savedColorPalettes
            const currentSpaceSnapshot = await get(spaceRef);
            const currentSpaceData = currentSpaceSnapshot.exists() ? currentSpaceSnapshot.val() : {};
            
            const updatedSpaceData = {
                ...spaceInfo,
                spaceName: spaceSettings.spaceName,
                description: spaceSettings.description,
                customDomain: spaceSettings.customDomain,
                domainStatus: spaceSettings.domainStatus,
                designSettings: designSettings,
                
                // NEW: Logo and branding settings
                customLogo: spaceSettings.customLogo,
                customLogoWidth: spaceSettings.customLogoWidth,
                customLogoHeight: spaceSettings.customLogoHeight,
                showCustomLogo: spaceSettings.showCustomLogo,
                siteName: spaceSettings.siteName,
                
                // NEW: Custom Meta & SEO settings
                customFavicon: spaceSettings.customFavicon,
                siteTitle: spaceSettings.siteTitle,
                siteDescription: spaceSettings.siteDescription,
                ogImage: spaceSettings.ogImage,
                
                updatedAt: new Date().toISOString(),
                
                // 🛡️ PRESERVE EXISTING SAVED PALETTES
                savedColorPalettes: currentSpaceData.savedColorPalettes || {}
            };
            
            console.log('🛡️ [SPACE-SAVE] Preserving savedColorPalettes:', Object.keys(currentSpaceData.savedColorPalettes || {}));
            
            await set(spaceRef, updatedSpaceData);

            // 🔥 NEW: Copy custom meta + branding settings to public space for unauthenticated access
            if (spaceSettings.siteTitle || spaceSettings.siteDescription || spaceSettings.customFavicon || spaceSettings.ogImage || spaceSettings.customLogo) {
                const publicSpaceRef = dbRef(db, `/publicSpaces/${spaceId}`);
                const publicMetaData = {
                    siteTitle: spaceSettings.siteTitle || '',
                    siteDescription: spaceSettings.siteDescription || '',
                    customFavicon: spaceSettings.customFavicon || '',
                    ogImage: spaceSettings.ogImage || '',
                    siteName: spaceSettings.siteName || spaceSettings.spaceName || '',
                    // 🔥 NEW: Copy custom logo settings for public access
                    customLogo: spaceSettings.customLogo || '',
                    customLogoWidth: spaceSettings.customLogoWidth || 120,
                    customLogoHeight: spaceSettings.customLogoHeight || 60,
                    showCustomLogo: spaceSettings.showCustomLogo !== false,
                    updatedAt: new Date().toISOString()
                };
                
                await set(publicSpaceRef, publicMetaData);
                console.log('🏷️ [SPACE-SAVE] Custom meta + branding settings copied to public space:', publicMetaData);
            }
            
            // 🔤 NEW: Copy custom fonts to public space for unauthenticated access
            try {
                const userCustomFontsRef = dbRef(db, `/users/${currentUser.uid}/customFonts`);
                const customFontsSnapshot = await get(userCustomFontsRef);
                
                if (customFontsSnapshot.exists()) {
                    const customFontsData = customFontsSnapshot.val();
                    const publicFontsRef = dbRef(db, `/publicSpaces/${spaceId}/customFonts`);
                    await set(publicFontsRef, customFontsData);
                    console.log('🔤 [SPACE-SAVE] Custom fonts copied to public space:', Object.keys(customFontsData));
                } else {
                    // Clear public fonts if no custom fonts exist
                    const publicFontsRef = dbRef(db, `/publicSpaces/${spaceId}/customFonts`);
                    await set(publicFontsRef, null);
                    console.log('🔤 [SPACE-SAVE] No custom fonts found, cleared public space fonts');
                }
            } catch (fontError) {
                console.warn('⚠️ [SPACE-SAVE] Error copying custom fonts to public space:', fontError);
            }

            // Apply settings to all pages in this space
            await applySettingsToAllPages();

            console.log('✅ Space settings saved successfully');
            
            // 🔤 FONT TRACKING: Reset font change tracking after successful save
            setOriginalFonts({
                globalBigFont: spaceSettings.globalBigFont,
                globalSmallFont: spaceSettings.globalSmallFont
            });
            setFontsChanged(false);
            setBigFontChanged(false);
            setSmallFontChanged(false);
            
            // 🎨 COLOR TRACKING: Reset color change tracking after successful save
            setOriginalColors(spaceSettings.colors);
            setOriginalColorAssignments(spaceSettings.colorAssignments);
            setColorsChanged(false);
            setColorAssignmentsChanged(false);
            
            console.log('🔤 [FONT-TRACKING] Reset after save:', {
                newOriginalFonts: {
                    globalBigFont: spaceSettings.globalBigFont,
                    globalSmallFont: spaceSettings.globalSmallFont
                },
                fontsChanged: false,
                bigFontChanged: false,
                smallFontChanged: false
            });
            
            console.log('🎨 [COLOR-TRACKING] Reset after save:', {
                newOriginalColors: spaceSettings.colors,
                newOriginalAssignments: spaceSettings.colorAssignments,
                colorsChanged: false,
                colorAssignmentsChanged: false
            });
            
            // Show success state in button
            setSaveSuccess(true);
            
            // Reset success state after showing feedback
            setTimeout(() => {
                setSaveSuccess(false);
            }, 2000);
            
        } catch (error) {
            console.error('❌ Error saving space settings:', error);
            // Reset success state on error
            setSaveSuccess(false);
            alert('Failed to save space settings. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };


    const applySettingsToAllPages = async () => {
        if (!currentUser || !spaceId) return;

        try {
            // Get all pages in this space
            const pagesRef = dbRef(db, `/pages/${currentUser.uid}/${spaceId}`);
            const pagesSnapshot = await get(pagesRef);
            
            if (pagesSnapshot.exists()) {
                const pages = pagesSnapshot.val();
                const updates = {};

                console.log('🔤 [FONT-UPDATE-CHECK] Fonts changed:', fontsChanged);
                console.log('🔤 [FONT-UPDATE-CHECK] Big font changed:', bigFontChanged);
                console.log('🔤 [FONT-UPDATE-CHECK] Small font changed:', smallFontChanged);
                console.log('🔤 [FONT-UPDATE-CHECK] Original fonts:', originalFonts);
                console.log('🔤 [FONT-UPDATE-CHECK] Current fonts:', {
                    globalBigFont: spaceSettings.globalBigFont,
                    globalSmallFont: spaceSettings.globalSmallFont
                });
                
                console.log('🎨 [COLOR-UPDATE-CHECK] Colors changed:', colorsChanged);
                console.log('🎨 [COLOR-UPDATE-CHECK] Color assignments changed:', colorAssignmentsChanged);
                console.log('🎨 [COLOR-UPDATE-CHECK] Original colors:', originalColors);
                console.log('🎨 [COLOR-UPDATE-CHECK] Original assignments:', originalColorAssignments);

                // Update each page with new space settings (RESPECT PAGE ISOLATION)
                Object.keys(pages).forEach(pageKey => {
                    const page = pages[pageKey];
                    
                    // 🛡️ CHECK: Hat diese Page eigene Color Assignments? (AI-generiert oder manuell festgelegt)
                    const hasOwnColorAssignments = page.colorAssignments && 
                        (page.generatedBy === 'AI' || page.hasCustomColorAssignments === true);
                    
                    if (hasOwnColorAssignments) {
                        console.log(`🛡️ [PAGE-ISOLATION] Page ${pageKey} has own color assignments - SKIPPING color updates`);
                        console.log(`🛡️ [PAGE-ISOLATION] Page assignments:`, page.colorAssignments);
                        console.log(`🛡️ [PAGE-ISOLATION] Generated by:`, page.generatedBy);
                        
                        // 🔤 NUR Fonts aktualisieren wenn sie tatsächlich geändert wurden
                        if (fontsChanged) {
                            console.log(`🔤 [FONT-UPDATE] Updating fonts for isolated page ${pageKey} (fonts were changed)`);
                            updates[`/pages/${currentUser.uid}/${spaceId}/${pageKey}/globalBigFont`] = spaceSettings.globalBigFont;
                            updates[`/pages/${currentUser.uid}/${spaceId}/${pageKey}/globalSmallFont`] = spaceSettings.globalSmallFont;
                        } else {
                            console.log(`🔤 [FONT-PRESERVE] Preserving fonts for isolated page ${pageKey} (no font changes)`);
                        }
                        
                        // Background immer aktualisieren (nicht font-related)
                        updates[`/pages/${currentUser.uid}/${spaceId}/${pageKey}/backgroundColor`] = spaceSettings.backgroundColor;
                        
                        // Widget-Updates mit PAGE-EIGENEN Color Assignments (nur geänderte Fonts aktualisieren, Farben bleiben page-eigen)
                        if (page.widgets && Array.isArray(page.widgets) && fontsChanged) {
                            const updatedWidgets = page.widgets.map(widget => {
                                return applySpaceSettingsToWidgetSync(widget, {
                                    colors: page.colors, // ✅ Page-eigene Farben verwenden (nicht überschreiben!)
                                    fonts: {
                                        globalBigFont: spaceSettings.globalBigFont,
                                        globalSmallFont: spaceSettings.globalSmallFont
                                    },
                                    colorAssignments: page.colorAssignments // ✅ Page-eigene Assignments verwenden (nicht überschreiben!)
                                }, {
                                    applyBigFont: bigFontChanged,    // ✅ NUR Big Font wenn geändert
                                    applySmallFont: smallFontChanged, // ✅ NUR Small Font wenn geändert
                                    applyColors: false               // ❌ Farben NICHT überschreiben (page-eigen)
                                });
                            });
                            
                            updates[`/pages/${currentUser.uid}/${spaceId}/${pageKey}/widgets`] = updatedWidgets;
                            console.log(`🛡️ [PAGE-ISOLATION] Updated ${updatedWidgets.length} widgets in isolated page ${pageKey} - SELECTIVE FONTS (big: ${bigFontChanged}, small: ${smallFontChanged}, colors preserved)`);
                        } else if (page.widgets && Array.isArray(page.widgets)) {
                            console.log(`🛡️ [PAGE-ISOLATION] Preserving widgets in isolated page ${pageKey} (no font changes, colors stay isolated)`);
                        }
                    } else {
                        console.log(`🔄 [SPACE-SYNC] Page ${pageKey} inherits space settings - applying updates`);
                        
                        // 🔤 Standard Space Settings anwenden - aber Fonts nur wenn geändert
                        if (fontsChanged) {
                            console.log(`🔤 [FONT-UPDATE] Updating fonts for synced page ${pageKey} (fonts were changed)`);
                            updates[`/pages/${currentUser.uid}/${spaceId}/${pageKey}/globalBigFont`] = spaceSettings.globalBigFont;
                            updates[`/pages/${currentUser.uid}/${spaceId}/${pageKey}/globalSmallFont`] = spaceSettings.globalSmallFont;
                        } else {
                            console.log(`🔤 [FONT-PRESERVE] Preserving fonts for synced page ${pageKey} (no font changes)`);
                        }
                        
                        // 🎨 Farben nur aktualisieren wenn sie geändert wurden
                        if (colorsChanged) {
                            console.log(`🎨 [COLOR-UPDATE] Updating colors for synced page ${pageKey} (colors were changed)`);
                            updates[`/pages/${currentUser.uid}/${spaceId}/${pageKey}/colors`] = spaceSettings.colors;
                        } else {
                            console.log(`🎨 [COLOR-PRESERVE] Preserving colors for synced page ${pageKey} (no color changes)`);
                        }
                        
                        // 🎨 Color Assignments nur aktualisieren wenn sie geändert wurden
                        if (colorAssignmentsChanged) {
                            console.log(`🎨 [ASSIGNMENT-UPDATE] Updating color assignments for synced page ${pageKey} (assignments were changed)`);
                            updates[`/pages/${currentUser.uid}/${spaceId}/${pageKey}/colorAssignments`] = spaceSettings.colorAssignments;
                        } else {
                            console.log(`🎨 [ASSIGNMENT-PRESERVE] Preserving color assignments for synced page ${pageKey} (no assignment changes)`);
                        }
                        
                        // Background immer aktualisieren (nicht kritisch)
                        updates[`/pages/${currentUser.uid}/${spaceId}/${pageKey}/backgroundColor`] = spaceSettings.backgroundColor;
                        
                        // Widget-Updates mit SPACE Settings (selektiv je nach Änderung)
                        if (page.widgets && Array.isArray(page.widgets) && (fontsChanged || colorsChanged || colorAssignmentsChanged)) {
                            const updatedWidgets = page.widgets.map(widget => {
                                return applySpaceSettingsToWidgetSync(widget, {
                                    colors: spaceSettings.colors, // ✅ Space-Farben verwenden
                                    fonts: {
                                        globalBigFont: spaceSettings.globalBigFont,
                                        globalSmallFont: spaceSettings.globalSmallFont
                                    },
                                    colorAssignments: spaceSettings.colorAssignments // ✅ Space-Assignments verwenden
                                }, {
                                    applyBigFont: bigFontChanged,    // ✅ NUR Big Font wenn geändert
                                    applySmallFont: smallFontChanged, // ✅ NUR Small Font wenn geändert
                                    applyColors: colorsChanged || colorAssignmentsChanged   // ✅ NUR Farben aktualisieren wenn geändert
                                });
                            });
                            
                            updates[`/pages/${currentUser.uid}/${spaceId}/${pageKey}/widgets`] = updatedWidgets;
                            console.log(`🔄 [SPACE-SYNC] Updated ${updatedWidgets.length} widgets in synced page ${pageKey} - SELECTIVE (bigFont: ${bigFontChanged}, smallFont: ${smallFontChanged}, colors: ${colorsChanged || colorAssignmentsChanged})`);
                        } else if (page.widgets && Array.isArray(page.widgets)) {
                            console.log(`🔄 [SPACE-SYNC] Preserving widgets in synced page ${pageKey} (no changes to fonts, colors, or assignments)`);
                        }
                    }
                });

                // Batch update all pages
                if (Object.keys(updates).length > 0) {
                    await update(dbRef(db, '/'), updates);
                    console.log('✅ Applied settings to all pages and widgets in space');
                }
            }
        } catch (error) {
            console.error('❌ Error applying settings to pages:', error);
        }
    };

    const renderActiveTab = () => {
        switch (activeTab) {
            case 'design':
                return (
                    <div>
                        {/* NEW: Custom Logo & Branding Section */}
                        <div className="space-settings-custom-branding-section">
                            <h4 className="space-settings-branding-header">
                            Custom Logo & Branding
                            </h4>
                            <p className="space-settings-branding-description">
                                Upload your logo for professional branding on loading screens and public pages. 
                                Create a completely white-label experience for your visitors.
                            </p>
                            
                            <div className="space-settings-logo-upload-grid">
                                <div className="space-settings-logo-upload-zone">
                                    {/* Hidden file input */}
                                    <input
                                        ref={logoInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleLogoUpload}
                                        style={{ display: 'none' }}
                                    />
                                    
                                    <div 
                                        className="space-settings-logo-dropzone" 
                                        onClick={triggerLogoUpload}
                                    >
                                        
                                        {logoUploadSuccess ? (
                                            <div className="space-settings-logo-upload-success">
                                                <i className="fas fa-check-circle space-settings-success-icon"></i>
                                                <p className="space-settings-success-text">
                                                    Logo uploaded successfully!
                                                </p>
                                            </div>
                                        ) : isUploadingLogo ? (
                                            <div className="space-settings-logo-upload-progress">
                                                <div className="space-settings-progress-spinner"></div>
                                                <p className="space-settings-progress-text">
                                                    Uploading... {Math.round(logoUploadProgress)}%
                                                </p>
                                                <div className="space-settings-progress-bar">
                                                    <div 
                                                        className="space-settings-progress-fill"
                                                        style={{ width: `${logoUploadProgress}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        ) : spaceSettings.customLogo ? (
                                            <div className="space-settings-logo-preview-container">
                                                <img 
                                                    src={spaceSettings.customLogo} 
                                                    alt="Custom Logo Preview" 
                                                    className="space-settings-logo-preview-image"
                                                    style={{ 
                                                        maxWidth: `${spaceSettings.customLogoWidth || 120}px`,
                                                        maxHeight: `${spaceSettings.customLogoHeight || 60}px`
                                                    }}
                                                />
                                                <div className="space-settings-logo-edit-overlay">
                                                    <i className="fas fa-edit space-settings-logo-edit-icon"></i>
                                                    <span className="space-settings-logo-edit-text">
                                                        Click to change logo
                                                    </span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-settings-logo-placeholder">
                                                <i className="fas fa-image space-settings-logo-placeholder-icon"></i>
                                                <h5 className="space-settings-logo-placeholder-title">
                                                    Upload Custom Logo
                                                </h5>
                                                <p className="space-settings-logo-placeholder-subtitle">
                                                    PNG, JPG, SVG up to 5MB<br/>
                                                    Recommended: 300x150px
                                                </p>
                                                <div className="space-settings-upload-cta-button">
                                                    <i className="fas fa-cloud-upload-alt"></i>
                                                    Choose File
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                {spaceSettings.customLogo && (
                                    <div className="space-settings-logo-controls-panel">
                                        <div className="space-settings-branding-form-group">
                                            <label className="space-settings-branding-label">
                                                Site Name (for branding)
                                            </label>
                                            <input
                                                type="text"
                                                value={spaceSettings.siteName || ''}
                                                onChange={(e) => setSpaceSettings(prev => ({
                                                    ...prev,
                                                    siteName: e.target.value
                                                }))}
                                                placeholder="Enter your site name"
                                                className="space-settings-branding-input"
                                            />
                                        </div>
                                        
                                        <div className="space-settings-logo-size-controls">
                                            <div className="space-settings-slider-group">
                                                <label className="space-settings-slider-label">
                                                    Logo Width: {spaceSettings.customLogoWidth || 120}px
                                                </label>
                                                <input
                                                    type="range"
                                                    min="60"
                                                    max="300"
                                                    step="10"
                                                    value={spaceSettings.customLogoWidth || 120}
                                                    onChange={(e) => setSpaceSettings(prev => ({
                                                        ...prev,
                                                        customLogoWidth: parseInt(e.target.value)
                                                    }))}
                                                    className="space-settings-custom-slider"
                                                />
                                            </div>
                                            
                                            <div className="space-settings-slider-group">
                                                <label className="space-settings-slider-label">
                                                    Logo Height: {spaceSettings.customLogoHeight || 60}px
                                                </label>
                                                <input
                                                    type="range"
                                                    min="30"
                                                    max="150"
                                                    step="5"
                                                    value={spaceSettings.customLogoHeight || 60}
                                                    onChange={(e) => setSpaceSettings(prev => ({
                                                        ...prev,
                                                        customLogoHeight: parseInt(e.target.value)
                                                    }))}
                                                    className="space-settings-custom-slider"
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="space-settings-branding-form-group">
                                            <label className="space-settings-branding-checkbox-container">
                                                <input
                                                    type="checkbox"
                                                    checked={spaceSettings.showCustomLogo !== false}
                                                    onChange={(e) => setSpaceSettings(prev => ({
                                                        ...prev,
                                                        showCustomLogo: e.target.checked
                                                    }))}
                                                    className="space-settings-custom-checkbox"
                                                />
                                                <span className="space-settings-checkbox-label-text">
                                                    Show logo on loading screens
                                                </span>
                                            </label>
                                        </div>
                                        
                                        <button
                                            className="space-settings-remove-logo-button"
                                            onClick={() => {
                                                if (window.confirm('Remove custom logo? This will restore default branding.')) {
                                                    setSpaceSettings(prev => ({
                                                        ...prev,
                                                        customLogo: '',
                                                        siteName: ''
                                                    }));
                                                }
                                            }}
                                        >
                                            <i className="fas fa-trash space-settings-remove-logo-icon"></i>
                                            Remove Logo
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <FontsSettings 
                            selectedBigFont={selectedFont}
                            selectedSmallFont={selectedSmallFont}
                            onBigFontChange={handleBigFontChange}
                            onSmallFontChange={handleSmallFontChange}
                            pageSettings={spaceSettings}
                            onColorChange={handleColorChange}
                            onBackgroundColorChange={handleBackgroundColorChange}
                            isReadOnly={false}
                        />
                        
                        {/* Smart Color Palette Section */}
                        <div className="space-color-section">
                            <h4 className="color-section-title">Color Palette</h4>
                            
                            {/* Saved Palettes Preview Section */}
                            <ColorPaletteGenerator
                                currentColors={[
                                    spaceSettings.colors?.color1 || '#CDD1C4',
                                    spaceSettings.colors?.color2 || '#5C80BC',
                                    spaceSettings.colors?.color3 || '#EBF5DF',
                                    spaceSettings.colors?.color4 || '#30323D',
                                    spaceSettings.colors?.color5 || '#FFFFFF'
                                ]}
                                onColorsChange={(newColors, showFeedback = false, preserveAssignments = true) => {
                                    console.log('🎨 Colors changed in SpaceSettings:', newColors);
                                    console.log('🎨 Preserve assignments:', preserveAssignments);
                                    
                                    // 🎨 COLOR TRACKING: Check if colors have changed
                                    const hasColorsChanged = JSON.stringify(newColors) !== JSON.stringify(originalColors);
                                    setColorsChanged(hasColorsChanged);
                                    
                                    console.log('🎨 [COLOR-CHANGE] Palette colors changed:', {
                                        from: originalColors,
                                        to: newColors,
                                        changed: hasColorsChanged
                                    });
                                    
                                    // Update space settings - nur Farben ändern, Assignments beibehalten wenn preserveAssignments = true
                                    setSpaceSettings(prev => ({
                                        ...prev,
                                        colors: newColors
                                        // colorAssignments werden nur überschrieben wenn preserveAssignments = false
                                    }));
                                    
                                    // Apply to all pages in space
                                    if (showFeedback) {
                                        applySettingsToAllPages();
                                    }
                                }}
                                inline={true}
                                spaceId={spaceId}
                                onPaletteSaved={() => {
                                    // Refresh palette check when new palette is saved
                                    checkSavedPalettes();
                                }}
                                showGeneratorToggle={true}
                                hasSavedPalettes={hasSavedPalettes}
                            />
                        </div>

                        {/* Color Assignment Section */}
                        <ColorAssignmentSelector
                            colors={spaceSettings.colors}
                            currentAssignments={spaceSettings.colorAssignments}
                            onAssignmentsChange={(newAssignments) => {
                                console.log('🎨 Color assignments changed:', newAssignments);
                                
                                // 🎨 COLOR ASSIGNMENT TRACKING: Check if assignments have changed
                                const hasAssignmentsChanged = JSON.stringify(newAssignments) !== JSON.stringify(originalColorAssignments);
                                setColorAssignmentsChanged(hasAssignmentsChanged);
                                
                                console.log('🎨 [ASSIGNMENT-CHANGE] Color assignments changed:', {
                                    from: originalColorAssignments,
                                    to: newAssignments,
                                    changed: hasAssignmentsChanged
                                });
                                
                                setSpaceSettings(prev => ({
                                    ...prev,
                                    colorAssignments: newAssignments
                                }));
                            }}
                            showPreview={true}
                        />
                        
                    </div>
                );
            case 'info':
                return (
                    <div className="space-info-settings">
                        <div className="settings-section">
                            <h4>Space Information</h4>
                            <div className="form-group">
                                <label>Space Name</label>
                                <input
                                    type="text"
                                    value={spaceSettings.spaceName}
                                    onChange={(e) => handleSettingsChange('spaceName', e.target.value)}
                                    placeholder="Enter space name..."
                                />
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={spaceSettings.description}
                                    onChange={(e) => handleSettingsChange('description', e.target.value)}
                                    placeholder="Describe your space..."
                                    rows="3"
                                />
                            </div>
                            <div className="form-group">
                                <label>Space URL</label>
                                <div className="url-display">
                                    <span className="url-prefix">bonusclub.link/</span>
                                    <span className="url-username">{spaceInfo?.username}</span>
                                    <span className="url-suffix">/</span>
                                </div>
                            </div>
                        </div>

                        <div className="settings-section">
                            <h4>Business Information</h4>
                            <div className="form-group">
                                <label>Owner Name</label>
                                <input
                                    type="text"
                                    value={spaceSettings.ownerName}
                                    onChange={(e) => handleSettingsChange('ownerName', e.target.value)}
                                    placeholder="Business owner name..."
                                />
                                <small className="form-hint">The name of the business owner or contact person</small>
                            </div>
                            <div className="form-group">
                                <label>Business Context & Information</label>
                                <textarea
                                    value={spaceSettings.contextInfo}
                                    onChange={(e) => handleSettingsChange('contextInfo', e.target.value)}
                                    placeholder="Describe your business, services, target audience, unique selling points, or any specific information that helps define your brand and what you do..."
                                    rows="6"
                                />
                                <small className="form-hint">This information helps with AI-generated content and provides context for your business. Used in page descriptions and meta information.</small>
                            </div>
                        </div>
                        
                        {spaceSettings.customDomain && (
                            <div className="settings-section">
                                <h4>Domain Information</h4>
                                <div className="form-group">
                                    <label>Custom Domain</label>
                                    <div className="domain-display">
                                        <span className="domain-url">{spaceSettings.customDomain}</span>
                                        <span className={`domain-status ${spaceSettings.domainStatus}`}>
                                            {spaceSettings.domainStatus}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            case 'domain':
                return (
                    <div className="domain-settings">
                        <div className="settings-section">
                            <h4>Domain Management</h4>
                            
                            {/* Current Domain Status */}
                            {domainInfo ? (
                                <div className="current-domain-section">
                                    <div className="domain-info-card">
                                        <div className="domain-header">
                                            <h5>Connected Domain</h5>
                                            <span className={`domain-status-badge ${domainInfo.mapping?.status || 'active'}`}>
                                                {domainInfo.mapping?.proxyEnabled ? 'Unlimited Proxy' : 'Active'}
                                            </span>
                                        </div>
                                        <div className="domain-url-display">
                                            <span className="domain-url">{domainInfo.domain}</span>
                                            <button 
                                                className="visit-domain-btn"
                                                onClick={() => window.open(`https://${domainInfo.domain}`, '_blank')}
                                            >
                                                <i className="fas fa-external-link-alt"></i>
                                                Visit
                                            </button>
                                        </div>
                                        <div className="domain-actions">
                                            <button 
                                                className="cloudflare-manager-btn"
                                                onClick={() => setShowCloudflareDomainManager(true)}
                                            >
                                                <i className="fas fa-cloud"></i>
                                                Cloudflare Manager
                                            </button>
                                            <button 
                                                className="show-dns-btn"
                                                onClick={() => {
                                                    if (domainInfo?.domain) {
                                                        const instructions = generateProxyDNSInstructions(domainInfo.domain);
                                                        setDnsInstructions(instructions);
                                                        setShowDNSInstructions(true);
                                                    }
                                                }}
                                            >
                                                <i className="fas fa-cog"></i>
                                                DNS Setup
                                            </button>
                                            <button 
                                                className="disconnect-btn"
                                                onClick={handleDisconnectDomain}
                                            >
                                                <i className="fas fa-unlink"></i>
                                                Disconnect Domain
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Permanent DNS Instructions Section */}
                                    <div className="permanent-dns-section">
                                        <h5>
                                            <i className="fas fa-cog"></i>
                                            DNS Configuration for {domainInfo.domain}
                                        </h5>
                                        <p className="dns-intro">Add these DNS records at your domain provider to activate your custom domain:</p>
                                        
                                        {(() => {
                                            const instructions = generateProxyDNSInstructions(domainInfo.domain);
                                            return (
                                                <div className="dns-records-compact">
                                                    {instructions.dns.map((record, index) => (
                                                        <div key={index} className={`dns-record-compact priority-${record.priority.toLowerCase()}`}>
                                                            <div className="dns-record-header-compact">
                                                                <span className="dns-record-type-compact">{record.type}</span>
                                                                <span className={`dns-priority-badge-compact priority-${record.priority.toLowerCase()}`}>
                                                                    {record.priority}
                                                                </span>
                                                            </div>
                                                            <div className="dns-record-content-compact">
                                                                <div className="dns-field-compact">
                                                                    <label>Name:</label>
                                                                    <code>{record.name}</code>
                                                                </div>
                                                                <div className="dns-field-compact">
                                                                    <label>Value:</label>
                                                                    <code>{record.value}</code>
                                                                    <button 
                                                                        className="copy-dns-compact"
                                                                        onClick={(event) => {
                                                                            navigator.clipboard.writeText(record.value);
                                                                            const btn = event.target.closest('.copy-dns-compact');
                                                                            const originalText = btn.innerHTML;
                                                                            btn.innerHTML = '<i class="fas fa-check"></i>';
                                                                            setTimeout(() => {
                                                                                btn.innerHTML = originalText;
                                                                            }, 2000);
                                                                        }}
                                                                        title="Copy DNS value"
                                                                    >
                                                                        <i className="fas fa-copy"></i>
                                                                    </button>
                                                                </div>
                                                                <div className="dns-field-compact">
                                                                    <label>TTL:</label>
                                                                    <code>{record.ttl}s</code>
                                                                </div>
                                                            </div>
                                                            <p className="dns-description-compact">{record.description}</p>
                                                        </div>
                                                    ))}
                                                    
                                                    <div className="dns-help-compact">
                                                        <div className="dns-timing-compact">
                                                            <i className="fas fa-clock"></i>
                                                            <span>DNS propagation: 2-5 minutes</span>
                                                        </div>
                                                        <div className="dns-test-compact">
                                                            <i className="fas fa-external-link-alt"></i>
                                                            <a 
                                                                href={instructions.testUrl} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                            >
                                                                Test Domain
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            ) : (
                                <div className="connect-domain-section">
                                    <div className="connect-form">
                                        <h5>Connect Custom Domain</h5>
                                        <p>Connect your own domain to this space for professional branding.</p>
                                        
                                        <div className="form-group">
                                            <label>Domain Name</label>
                                            <input
                                                type="text"
                                                value={newDomain}
                                                onChange={(e) => setNewDomain(e.target.value)}
                                                placeholder="example.com"
                                                className={domainError ? 'error' : ''}
                                            />
                                            {domainError && (
                                                <div className="error-message">
                                                    <i className="fas fa-exclamation-triangle"></i>
                                                    {domainError}
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="proxy-info">
                                            <div className="proxy-enabled-badge">
                                                <i className="fas fa-check-circle"></i>
                                                Cloudflare Proxy (Unlimited) - Enabled
                                            </div>
                                            <p className="proxy-description">
                                                Automatic unlimited bandwidth and better performance for your domain.
                                            </p>
                                        </div>
                                        
                                        <button 
                                            className="connect-domain-btn"
                                            onClick={handleConnectDomain}
                                            disabled={connectingDomain || !newDomain.trim()}
                                        >
                                            {connectingDomain ? (
                                                <>
                                                    <i className="fas fa-spinner fa-spin"></i>
                                                    Connecting...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-link"></i>
                                                    Connect Domain
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            {/* Space URLs Overview */}
                            <div className="urls-overview">
                                <h5>Your Space URLs</h5>
                                <div className="url-list">
                                    <div className="url-item bonusclub-url">
                                        <div className="url-info">
                                            <span className="url-label">BonusClub Link</span>
                                            <span className="url-value">bonusclub.link/{spaceInfo?.username}/</span>
                                        </div>
                                        <button 
                                            className="copy-url-btn"
                                            onClick={() => navigator.clipboard.writeText(`${window.location.origin}/${spaceInfo?.username}/`)}
                                        >
                                            <i className="fas fa-copy"></i>
                                        </button>
                                    </div>
                                    
                                    {domainInfo && (
                                        <div className="url-item custom-url">
                                            <div className="url-info">
                                                <span className="url-label">Custom Domain</span>
                                                <span className="url-value">{domainInfo.domain}</span>
                                            </div>
                                            <button 
                                                className="copy-url-btn"
                                                onClick={() => navigator.clipboard.writeText(`https://${domainInfo.domain}/`)}
                                            >
                                                <i className="fas fa-copy"></i>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'meta':
                return (
                    <div className="meta-settings">
                        <div className="settings-section">
                            <h4>🏷️ Custom Meta Tags & SEO</h4>
                            <p>Customize how your website appears in browser tabs and social media when using custom domains.</p>
                            
                            {/* Site Title */}
                            <div className="form-group">
                                <label>
                                    <i className="fas fa-heading"></i>
                                    Site Title (Browser Tab)
                                </label>
                                <input
                                    type="text"
                                    value={spaceSettings.siteTitle || ''}
                                    onChange={(e) => handleSettingsChange('siteTitle', e.target.value)}
                                    placeholder="My Amazing Website"
                                    maxLength={60}
                                />
                                <small>This appears in the browser tab and search results ({(spaceSettings.siteTitle || '').length}/60)</small>
                            </div>

                            {/* Site Description */}
                            <div className="form-group">
                                <label>
                                    <i className="fas fa-align-left"></i>
                                    Site Description (Meta Description)
                                </label>
                                <textarea
                                    value={spaceSettings.siteDescription || ''}
                                    onChange={(e) => handleSettingsChange('siteDescription', e.target.value)}
                                    placeholder="A brief description of your website for search engines and social media"
                                    maxLength={160}
                                    rows={3}
                                />
                                <small>Used in search results and social media previews ({(spaceSettings.siteDescription || '').length}/160)</small>
                            </div>

                            {/* Custom Favicon */}
                            <div className="form-group">
                                <label>
                                    <i className="fas fa-image"></i>
                                    Custom Favicon
                                </label>
                                <div className="favicon-upload-section">
                                    <input
                                        type="text"
                                        value={spaceSettings.customFavicon || ''}
                                        onChange={(e) => handleSettingsChange('customFavicon', e.target.value)}
                                        placeholder="https://example.com/favicon.ico"
                                    />
                                    <button 
                                        className="upload-favicon-btn"
                                        onClick={() => document.getElementById('favicon-upload').click()}
                                    >
                                        <i className="fas fa-upload"></i>
                                        Upload
                                    </button>
                                    <input
                                        id="favicon-upload"
                                        type="file"
                                        accept="image/*,.ico"
                                        style={{ display: 'none' }}
                                        onChange={handleFaviconUpload}
                                    />
                                </div>
                                {spaceSettings.customFavicon && (
                                    <div className="favicon-preview">
                                        <img 
                                            src={spaceSettings.customFavicon} 
                                            alt="Favicon Preview" 
                                            style={{ width: '32px', height: '32px' }}
                                        />
                                        <span>Preview</span>
                                    </div>
                                )}
                                <small>Appears in browser tabs and bookmarks. Recommended: 32x32px ICO or PNG</small>
                            </div>

                            {/* OG Image for Social Media */}
                            <div className="form-group">
                                <label>
                                    <i className="fas fa-share-alt"></i>
                                    Social Media Image (OG Image)
                                </label>
                                <div className="og-image-upload-section">
                                    <input
                                        type="text"
                                        value={spaceSettings.ogImage || ''}
                                        onChange={(e) => handleSettingsChange('ogImage', e.target.value)}
                                        placeholder="https://example.com/og-image.jpg"
                                    />
                                    <button 
                                        className="upload-og-btn"
                                        onClick={() => document.getElementById('og-upload').click()}
                                    >
                                        <i className="fas fa-upload"></i>
                                        Upload
                                    </button>
                                    <input
                                        id="og-upload"
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={handleOGImageUpload}
                                    />
                                </div>
                                {spaceSettings.ogImage && (
                                    <div className="og-image-preview">
                                        <img 
                                            src={spaceSettings.ogImage} 
                                            alt="OG Image Preview" 
                                            style={{ width: '200px', height: 'auto', maxHeight: '100px', objectFit: 'cover' }}
                                        />
                                        <span>Social Media Preview</span>
                                    </div>
                                )}
                                <small>Used when sharing your website on social media. Recommended: 1200x630px</small>
                            </div>

                            {/* Preview Section */}
                            <div className="meta-preview-section">
                                <h5>
                                    <i className="fas fa-eye"></i>
                                    Preview
                                </h5>
                                <div className="meta-preview-card">
                                    <div className="browser-tab-preview">
                                        <div className="tab-preview">
                                            {spaceSettings.customFavicon && (
                                                <img src={spaceSettings.customFavicon} alt="favicon" className="tab-favicon" />
                                            )}
                                            <span className="tab-title">
                                                {spaceSettings.siteTitle || spaceSettings.spaceName || 'Your Website'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="social-preview">
                                        <div className="social-card">
                                            {spaceSettings.ogImage && (
                                                <div className="social-image">
                                                    <img src={spaceSettings.ogImage} alt="OG Preview" />
                                                </div>
                                            )}
                                            <div className="social-content">
                                                <h6>{spaceSettings.siteTitle || spaceSettings.spaceName || 'Your Website'}</h6>
                                                <p>{spaceSettings.siteDescription || 'Your website description will appear here'}</p>
                                                <span className="social-domain">{spaceSettings.customDomain || 'your-domain.com'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'danger':
                return (
                    <div className="danger-zone-content">
                        <div className="danger-zone-header">
                            <div className="danger-icon">
                                <i className="fas fa-exclamation-triangle"></i>
                            </div>
                            <div className="danger-text">
                                <h3>Danger Zone</h3>
                                <p>Irreversible actions that will permanently affect your space</p>
                            </div>
                        </div>

                        <div className="danger-actions">
                            <div className="danger-action-card">
                                <div className="danger-action-info">
                                    <h4>Delete Space</h4>
                                    <p>Permanently delete this space and all its content. This action cannot be undone.</p>
                                    
                                    {pages.length > 0 && (
                                        <div className="deletion-preview">
                                            <h5>This will delete:</h5>
                                            <ul>
                                                <li><i className="fas fa-file-alt"></i> {pages.length} pages</li>
                                                <li><i className="fas fa-user"></i> Username "{spaceInfo?.username}"</li>
                                                <li><i className="fas fa-cog"></i> All settings and content</li>
                                                {domainInfo && (
                                                    <li><i className="fas fa-globe"></i> Custom domain "{domainInfo.domain}"</li>
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                                
                                <button 
                                    className="danger-delete-btn"
                                    onClick={() => setShowDeleteModal(true)}
                                >
                                    <i className="fas fa-trash-alt"></i>
                                    Delete Space
                                </button>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    // Removed full-screen loading overlay - now using button spinner only

    return (
        <div className="space-settings-page">
            {/* Header */}
            <div className="space-settings-header">
                <div className="header-content">
                    <div className="header-info">
                        <button 
                            className="back-button"
                            onClick={() => navigate(`/space/${spaceId}`)}
                        >
                            <i className="fas fa-arrow-left"></i>
                            Back to Space
                        </button>
                        <h1>Space Settings</h1>
                        <p>Global settings for <strong>{spaceSettings.spaceName || spaceId}</strong></p>
                    </div>
                </div>
                
                <div className="settings-tabs">
                    <button 
                        className={`tab-button ${activeTab === 'design' ? 'active' : ''}`}
                        onClick={() => setActiveTab('design')}
                    >
                        <i className="fas fa-palette"></i>
                        Design & Fonts
                    </button>
                        <button 
                            className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
                            onClick={() => setActiveTab('info')}
                        >
                            <i className="fas fa-info-circle"></i>
                            Space Info
                        </button>
                        <button 
                            className={`tab-button ${activeTab === 'domain' ? 'active' : ''}`}
                            onClick={() => setActiveTab('domain')}
                        >
                            <i className="fas fa-globe"></i>
                            Domain
                        </button>
                        <button 
                            className={`tab-button ${activeTab === 'meta' ? 'active' : ''}`}
                            onClick={() => setActiveTab('meta')}
                        >
                            <i className="fas fa-tags"></i>
                            Custom Meta
                        </button>
                        <button 
                            className={`tab-button danger ${activeTab === 'danger' ? 'active' : ''}`}
                            onClick={() => setActiveTab('danger')}
                        >
                            <i className="fas fa-exclamation-triangle"></i>
                            Danger Zone
                        </button>
                </div>
            </div>

            {/* Content */}
            <div className="space-settings-content">
                {renderActiveTab()}
            </div>

            {/* Footer */}
            <div className="space-page-footer">
                <div className="space-page-footer-info">
                    <i className="fas fa-info-circle"></i>
                    <span>These settings will apply to all pages in this space</span>
                </div>
                <div className="space-page-footer-actions">
                    <button 
                        className="space-page-cancel-button"
                        onClick={() => navigate(`/space/${spaceId}`)}
                    >
                        Cancel
                    </button>
                    <button 
                        className={`space-settings-save-btn ${isLoading ? 'space-settings-save-loading' : ''} ${saveSuccess ? 'space-settings-save-success' : ''}`}
                        onClick={saveSpaceSettings}
                        disabled={isLoading || saveSuccess}
                    >
                        {isLoading ? (
                            <>
                                <div className="space-settings-save-spinner"></div>
                                Saving...
                            </>
                        ) : saveSuccess ? (
                            <>
                                <i className="fas fa-check space-settings-save-icon"></i>
                                Saved Successfully!
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save space-settings-save-icon"></i>
                                Save Settings
                            </>
                        )}
                    </button>
                </div>
            </div>
            

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-danger">
                    <div className="modal-content-danger">
                        <div className="modal-header-danger">
                            <div className="modal-icon-danger">
                                <i className="fas fa-exclamation-triangle"></i>
                            </div>
                            <h3>Delete Space</h3>
                            <button 
                                className="modal-close-btn"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="modal-body-danger">
                            <p>Are you sure you want to permanently delete <strong>{spaceInfo?.spaceName || spaceInfo?.username}</strong>?</p>
                            
                            {pages.length > 0 && (
                                <div className="deletion-preview-modal">
                                    <h4>This will delete:</h4>
                                    <ul>
                                        <li><i className="fas fa-file-alt"></i> {pages.length} pages</li>
                                        <li><i className="fas fa-user"></i> Username "{spaceInfo?.username}"</li>
                                        <li><i className="fas fa-cog"></i> All settings and content</li>
                                        {domainInfo && (
                                            <li><i className="fas fa-globe"></i> Custom domain "{domainInfo.domain}"</li>
                                        )}
                                    </ul>
                                </div>
                            )}
                            
                            <div className="warning-box-danger">
                                <i className="fas fa-exclamation-circle"></i>
                                <span>This action cannot be undone!</span>
                            </div>
                        </div>
                        
                        <div className="modal-actions-danger">
                            <button 
                                className="btn-danger-secondary"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deleting}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn-danger-primary"
                                onClick={handleDeleteSpace}
                                disabled={deleting}
                            >
                                <i className="fas fa-trash-alt"></i>
                                {deleting ? 'Deleting...' : 'Delete Forever'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* DNS Instructions Modal */}
            {showDNSInstructions && dnsInstructions && (
                <div className="modal-overlay">
                    <div className="modal-content dns-instructions-modal">
                        <div className="modal-header">
                            <h3>
                                <i className="fas fa-cog"></i>
                                DNS Setup Instructions
                            </h3>
                            <button 
                                className="modal-close-btn"
                                onClick={() => setShowDNSInstructions(false)}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="dns-instructions-content">
                                <div className="dns-intro">
                                    <div className="domain-info-header">
                                        <div className="domain-info-main">
                                            <h4>{dnsInstructions.domain}</h4>
                                            <span className="domain-type-badge">{dnsInstructions.domainType}</span>
                                        </div>
                                        <p>Add the following DNS records at your domain provider to connect your domain:</p>
                                    </div>
                                </div>
                                
                                <div className="dns-records-section">
                                    <h4>DNS Records to Add:</h4>
                                    {dnsInstructions.dns.map((record, index) => (
                                        <div key={index} className={`dns-record-card priority-${record.priority.toLowerCase()}`}>
                                            <div className="dns-record-header">
                                                <div className="dns-record-title">
                                                    <span className="dns-record-type">{record.type}</span>
                                                    <span className={`dns-priority-badge priority-${record.priority.toLowerCase()}`}>
                                                        {record.priority}
                                                    </span>
                                                </div>
                                                <button 
                                                    className="copy-dns-btn"
                                                    onClick={(event) => {
                                                        navigator.clipboard.writeText(record.value);
                                                        // Show copy feedback
                                                        const btn = event.target.closest('.copy-dns-btn');
                                                        const originalText = btn.innerHTML;
                                                        btn.innerHTML = '<i class="fas fa-check"></i> Copied!';
                                                        setTimeout(() => {
                                                            btn.innerHTML = originalText;
                                                        }, 2000);
                                                    }}
                                                >
                                                    <i className="fas fa-copy"></i>
                                                    Copy Value
                                                </button>
                                            </div>
                                            <div className="dns-record-details">
                                                <div className="dns-field">
                                                    <label>Name/Host:</label>
                                                    <code>{record.name}</code>
                                                </div>
                                                <div className="dns-field">
                                                    <label>Value/Target:</label>
                                                    <code>{record.value}</code>
                                                </div>
                                                <div className="dns-field">
                                                    <label>TTL:</label>
                                                    <code>{record.ttl} seconds</code>
                                                </div>
                                            </div>
                                            <p className="dns-record-description">{record.description}</p>
                                        </div>
                                    ))}
                                </div>
                                
                                <div className="dns-steps-section">
                                    <h4>Step-by-Step Instructions:</h4>
                                    <ol className="dns-steps-list">
                                        {dnsInstructions.instructions.map((step, index) => (
                                            <li key={index}>{step}</li>
                                        ))}
                                    </ol>
                                </div>
                                
                                <div className="dns-benefits-section">
                                    <h4>Benefits of Our Proxy System:</h4>
                                    <ul className="dns-benefits-list">
                                        {dnsInstructions.benefits.map((benefit, index) => (
                                            <li key={index}>{benefit}</li>
                                        ))}
                                    </ul>
                                </div>
                                
                                {dnsInstructions.urlStructure && (
                                    <div className="dns-url-structure-section">
                                        <h4>URL Structure:</h4>
                                        <div className="url-structure-info">
                                            <p className="url-structure-description">{dnsInstructions.urlStructure.info}</p>
                                            <div className="url-examples">
                                                <h5>Your pages will be available at:</h5>
                                                <ul className="url-examples-list">
                                                    {dnsInstructions.urlStructure.examples.map((example, index) => (
                                                        <li key={index} className="url-example">
                                                            <code>{example}</code>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {dnsInstructions.troubleshooting && (
                                    <div className="dns-troubleshooting-section">
                                        <h4>Troubleshooting:</h4>
                                        <ul className="dns-troubleshooting-list">
                                            {dnsInstructions.troubleshooting.map((tip, index) => (
                                                <li key={index}>{tip}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                <div className="dns-timing-info">
                                    <div className="timing-card">
                                        <i className="fas fa-clock"></i>
                                        <div>
                                            <strong>Estimated Time:</strong>
                                            <p>{dnsInstructions.estimatedTime}</p>
                                        </div>
                                    </div>
                                    <div className="test-url-card">
                                        <i className="fas fa-external-link-alt"></i>
                                        <div>
                                            <strong>Test URL:</strong>
                                            <a 
                                                href={dnsInstructions.testUrl} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="test-domain-link"
                                            >
                                                {dnsInstructions.testUrl}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary"
                                onClick={() => setShowDNSInstructions(false)}
                            >
                                Close
                            </button>
                            <button 
                                className="btn-primary"
                                onClick={() => window.open(dnsInstructions.testUrl, '_blank')}
                            >
                                <i className="fas fa-external-link-alt"></i>
                                Test Domain
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cloudflare Domain Manager Modal */}
            {showCloudflareDomainManager && domainInfo && currentUser && (
                <div className="modal-overlay">
                    <CloudflareDomainManager
                        domain={domainInfo.domain}
                        userId={currentUser.uid}
                        username={spaceInfo?.username || spaceId}
                        spaceId={spaceId}
                        onClose={() => setShowCloudflareDomainManager(false)}
                    />
                </div>
            )}
        </div>
    );
};

export default SpaceSettings;
